/*
 * Copyright 2024 DeviceAtlas Limited. All rights reserved.
 * http://deviceatlas.com
 */

/* jshint strict: false */
var DeviceValidation = window.DeviceValidation || DeviceValidation || {};

DeviceValidation = (function(window, document, navigator, undefined) {

    var devicePropertiesAsString = "";
    var _DeviceValidation = {};
    var _DeviceAtlasCollector = null;
    var propertiesLoadComplete = false;
    var getPropertiesTimeoutInitialized = false;

    var isAppleDevice;
    var timeoutInitialized = false;
    var WAIT_ATTEMPTS = {
        SERVER_TO_SERVER: 0,
        AUDIO_REF: 0
    };
    var MAX_WAIT_ATTEMPTS = 10;
    var DEFAULT_TIME_TO_WAIT = 30;
    var loading = false;

    var MESSAGE_TYPES = {
        DA_MESSAGE: 'DA_MESSAGE',
        WAIT_AUDIO_REF: 'WAIT_AUDIO_REF',
        ON_LOAD: 'ON_LOAD'
    };

    var STORAGE_TYPE = {
        COOKIE: 'cookie',
        LOCAL_STORAGE: 'local-storage',
        NONE: 'none'
    };

    var DEFAULT_OPTIONS = {
        THIRTY_MINUTES_IN_MILLIS: 30 * 60 * 1000, // 30 Minutes
        TWENTY_FOUR_HOURS_IN_MILLIS: 24 * 60 * 60 * 1000, // 24 Hours
        CACHE_KEY: 'DV',
        CACHE_VALUE: '1'
    };

    var _PERMISSIONS = [
        'accelerometer',
        'accessibility-events',
        'ambient-light-sensor',
        'background-sync',
        'camera',
        'clipboard-read',
        'clipboard-write',
        'geolocation',
        'gyroscope',
        'magnetometer',
        'microphone',
        'midi',
        'notifications',
        'payment-handler',
        'persistent-storage',
        'push',
        'speaker-selection'
    ];

    var apiData = null;
    var permissions = null;

    var currentUrl;
    var attempt = 0;
    var prevErrors = [];
    var missingProperties = {};

    var _cache = {};

    var HttpStatus = {
        OK: 200,
        ACCEPTED: 202,
        BAD_REQUEST: 400,
        TOO_MANY_REQUEST: 429
    };

    var HttpRequestState = {
        UNSENT: 0,
        OPENED: 1,
        HEADERS_RECEIVED: 2,
        LOADING: 3,
        DONE: 4
    };

    var version = '1.0.23';

    var XMLHttpFactories = [
        function () { return new XMLHttpRequest();                    },
        function () { return new ActiveXObject('Msxml3.XMLHTTP');     },
        function () { return new ActiveXObject('Msxml2.XMLHTTP.6.0'); },
        function () { return new ActiveXObject('Msxml2.XMLHTTP.3.0'); },
        function () { return new ActiveXObject('Msxml2.XMLHTTP');     },
        function () { return new ActiveXObject('Microsoft.XMLHTTP');  }
    ];

    /**
     * Function to return the payload for the device validation check.
     * accepts a callback function
     *
     * if callback is passed in then the callback is called with data else the payload is returned
     *
     * This will be removed in a future release, please use the async method in the future, some of the properties we
     * collect require a synchronous approach. For a more accurate result please use the async approach.
     *
     **/
    _DeviceValidation.getData = function(callback) {

        if (!callback || propertiesLoadComplete || getPropertiesTimeoutInitialized) {
            return getPayloadData(callback, buildPayload);
        }

        getPropertiesTimeoutInitialized = true;

        var getPayloadDataCallback = function(callback, buildPayloadFunc) {
            return getPayloadData(callback, buildPayloadFunc);
        };

        setTimeout(getPayloadDataCallback, 1000, callback, buildPayload);
    };

    /**
     * Method that looks at if some properties are present or if we have waited long enough for some properties
     *
     * @param task - key inside the WAIT_ATTEMPTS object.
     *
     * @returns {boolean}
     */
    var haveCriticalPropertiesLoaded = function (task) {
        return hasExceededAttempts(task) || _hasAudioRef();
    };


    /**
     * Checks if a specified task has exceeded the number of attempts to see if the value is present
     *
     * @param task
     * @returns {boolean}
     */
    var hasExceededAttempts = function (task) {

        if (WAIT_ATTEMPTS[task] === undefined || WAIT_ATTEMPTS[task] === null) {
            return true;
        }

        return WAIT_ATTEMPTS[task] >= MAX_WAIT_ATTEMPTS;
    };

    /**
     * Returns whether library has been configured for serverToServer
     * @param options
     * @returns {*|{}|boolean}
     */
    var isServerToServer = function (options) {

        var dvOptions = options || (_DeviceValidation && _DeviceValidation.options) || {};

        return (dvOptions && (dvOptions.serverToServerEnabled || dvOptions.serverToServer)) || false;
    };

    var isValidLicenceKey = function (options) {

        if (_isCustomUrlMode()) {
            return true;
        }

        return options && options.licence && options.licence !== '<LICENCE_KEY>';
    };

    var getPayloadData = function (callback, buildPayloadFunc) {
        var payloadData = buildPayloadFunc(null);

        delete payloadData.info.dataCollectorKey;

        payloadData.info.serverToServer = true;

        // call it if a callback function was provided and return else return payload
        if (callback != null && typeof callback === 'function') {
            callback(payloadData);
            return;
        }
        return payloadData;
    };

    /**
     * Trigger to call the API check with the IMEI passed in. It will populate the data.telephony.imei and tac
     * properties if present.
     *
     * @param imei - IMEI value
     * @param overrides - contains options to override the default values in the library loaded in on page load.
     * @returns {Promise<unknown>}
     */
    _DeviceValidation.checkIMEI = function(imei, overrides) {
        var onSuccess = (overrides && overrides.onSuccess) || null;
        var onError = (overrides && overrides.onError) || null;
        var licence = (overrides && overrides.licence) || null;
        var payload = (overrides && overrides.payload) || null;
        var overrideLoading = (overrides && overrides.overrideLoading) || false;

        if (!onSuccess) {
            onSuccess = (_DeviceValidation.options && _DeviceValidation.options.onSuccess) || function() {};
        }

        if (!onError) {
            onError = (_DeviceValidation.options && _DeviceValidation.options.onError) || function() {};
        }

        if (!licence) {
            licence = (_DeviceValidation.options && _DeviceValidation.options.licence) || '';
        }
        licence = licence.trim();

        var isValidLicenceKey = (licence && licence.indexOf('LICENCE')) === -1 || false;

        if (!isImeiEnabled()) {
            throw 'IMEI option is not enabled, please enable it in config. Not sending api request';
        }
        if (!isValidLicenceKey) {
            throw 'Invalid licence key';
        }

        payload = _buildIMEIPayload(licence, imei, payload);

        return _DeviceValidation.check(licence, onSuccess, onError, payload, currentUrl, overrideLoading, null);
    };

    /**
     * Method which immediately calls the API to check the device.
     *
     * It is recommended to use this method on an event to give time to the DeviceAtlas library to load and collect
     * the device properties.
     *
     * If it is used on page load, it creates a race condition between the DeviceAtlas library and the API call.
     *
     * @param licence - licence key
     * @param onSuccess - callback function to call on success
     * @param onError - callback function to call on error
     * @param payload - [optional] Payload to send to the API.
     * @param currentUrl - [optional] Url to send the request to (used for failover)
     * @param overrideLoading - [optional] override the loading flag, this can help to prevent calling the api if there
     *                          is some form of external loading that should be needed to block this.
     * @param overridingURLs - [optional] Override the urls to send the request to.
     *
     * @returns {Promise|any|void}
     */
    _DeviceValidation.check = function(licence, onSuccess, onError, payload, currentUrl, overrideLoading, overridingURLs) {
        onSuccess = onSuccess || function(){};
        onError = onError || function(){};

        if (loading && !overrideLoading) {
            return;
        }

        var cachedResponse = _getCacheValue();

        var hasNoCache = STORAGE_TYPE.NONE === _getStorageType() || !cachedResponse;

        if (isPromiseSupported()) {
            return new Promise(function (resolve, reject) {

                if (hasNoCache) {
                    return doRequest(licence, onSuccess, onError, payload, currentUrl, resolve, reject, overridingURLs);
                }
                if (resolve) {
                    resolve(cachedResponse);
                }

                if (onSuccess) {
                    onSuccess(cachedResponse);
                }

                return cachedResponse;
            });
        }

        if (hasNoCache) {
            return doRequest(licence, onSuccess, onError, payload, currentUrl, null, null, overridingURLs);
        }

        if (onSuccess) {
            onSuccess(cachedResponse);
        }

        return cachedResponse;
    };

    /**
     * Adds an error element to the local buffer
     */
    var addError = function(errorObj) {
        prevErrors.push(errorObj);
    };

    var flushErrors = function() {
        prevErrors = [];
        loading = false;
        flushMissingProperties();
        resetAttempts();
    };

    var _resetConfig = function () {
        _DeviceValidation.options = undefined;
    };

    var _resetApiData = function () {
        apiData = null;
    };

    var _getDeviceValidation = function () {
        return _DeviceValidation;
    };

    var flushMissingProperties = function() {
        missingProperties = {};
    };

    var resetAttempts = function() {
        WAIT_ATTEMPTS = { SERVER_TO_SERVER: 0, AUDIO_REF: 0 };
    };

    var createXMLHTTPObject = function() {
        var xmlhttp = false;
        for (var i = 0; i < XMLHttpFactories.length; i++) {
            try {
                xmlhttp = XMLHttpFactories[i]();
            }
            catch (e) {
                continue;
            }
            break;
        }

        return xmlhttp;
    };

    var getApiUrls = function () {
        var apiUrls = (_DeviceValidation && _DeviceValidation.options && _DeviceValidation.options.urls) || [];

        if (apiUrls.length > 0) {
            return apiUrls;
        }
        return _getDefaultOptions().urls;
    };

    var doRequest = function(licence, onSuccess, onError, suppliedPayload, currentUrl, resolve, reject, urls) {

        var xhr = createXMLHTTPObject();

        if (!xhr) {
            // This browser does not support Ajax
            return;
        }
        loading = true;

        currentUrl = !!currentUrl ? currentUrl : (urls || getApiUrls())[attempt];

        xhr.open('POST', currentUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/json');

        xhr.onerror = function(e) {
            addError({hostUrl: currentUrl, httpStatus: 'N/A'});
            var response = {
                status: xhr.status,
                response: xhr.response
            };
            failover(licence, onSuccess, onError, suppliedPayload, response, reject, urls);
        };

        xhr.onreadystatechange = function () {

            if (xhr.readyState !== HttpRequestState.DONE) {
                return;
            }

            var response = {
                status: xhr.status,
                response: xhr.response
            };

            // on success
            if (xhr.status === HttpStatus.OK || xhr.status === HttpStatus.ACCEPTED) {
                _setTTL(response);

                attempt = 0;
                flushErrors();
                onSuccess(response);

                if (resolve) {
                    return resolve(response);
                }
                return response;
            }

            if (xhr.status === HttpStatus.TOO_MANY_REQUEST) {
                attempt = 0;
                flushErrors();
                onError(response);

                if (reject) {
                    return reject(response);
                }
                return response;
            }

            // on error
            if (xhr.status >= HttpStatus.BAD_REQUEST) {
                addError({hostUrl: currentUrl, httpStatus: xhr.status});
                return failover(licence, onSuccess, onError, payload, response, reject, urls);
            }
        };
        var payload = suppliedPayload;

        if (!payload) {
            payload = buildPayload(licence);
        }

        var dvLibraryType = 'web';

        if (isImeiEnabled() && !!payload.data.telephony.imei) {
            dvLibraryType = dvLibraryType + '-and-imei';
        }
        xhr.setRequestHeader('library-type', dvLibraryType);

        if (isPromiseSupported() && isMediaDevicesAvailable()) {

            // Handling how the media devices are obtained. Forcing a wait on promise before sending to API
            navigator.mediaDevices.enumerateDevices()
                    .then(function(devices) {
                        var text = '';
                        devices.forEach(function(device) {
                            text = text + device.kind + ': ' + device.label + ' id = ' + device.deviceId + "\n";
                        });
                        return text;
                    })
                    .then(function(mediaDevices) {
                        payload.data.web.webview.jsProps["navigator.mediaDevices"] = mediaDevices;
                        var jsonPayload = JSON.stringify(payload);
                        xhr.send(jsonPayload);
                    });
        }
        else {
            // Either promises not supported or mediadevices is undefined.
            var jsonPayload = JSON.stringify(payload);
            xhr.send(jsonPayload);
        }
    };

    /**
     * Builds the telephony block of the payload.
     *
     * When an imei arg is passed in, then it will populate these fields too.
     *
     * The TAC is the first 8 digits of the IMEI.
     *
     * @param imei
     * @returns {{isVoiceCapable: *, tac: null, imei: null, phoneCount: number}}
     * @private
     */
    var _buildTelephony = function(imei) {
        var telephony = {
            isVoiceCapable: isMobile(navigator.userAgent, navigator.platform, navigator.vendor),
            phoneCount: 1
        };

        var imeiValue = (imei && imei.replace(/\s/g, '')) || null;

        if (imeiValue) {
            telephony.imei = imeiValue;

            if (imeiValue.length > 7) {
                telephony.tac = imeiValue.substring(0, 8);
            }
        }

        return telephony;
    };

    /**
     * Adds in the IMEI to the payload passed in or returns the new payload block
     * @param licence
     * @param imei
     * @param payload
     * @returns {{data: {memory: {}, sensors: [], build: {brand: *}, usb: {}, web: {webview: {jsProps: {}}, webviewUserAgent, systemUserAgent}, telephony: {isVoiceCapable: *, tac: null, imei: null, phoneCount: number}, display: {xDpi: *, heightPixels: number, yDpi: *, widthPixels: number}, cpu: {numCores: *, availableProcessors: *}, storage: {}, camera: [], gpu: {}}, info: {date: string, dataCollectorKey: *, serverToServer: boolean, source: string, version: string}}|*}
     * @private
     */
    var _buildIMEIPayload = function(licence, imei, payload) {

        if (!payload || Object.keys(payload).length === 0) {
            return buildPayload(licence, imei);
        }

        if (!imei || imei.length < 1) {
            return payload;
        }

        var telephony = _buildTelephony(imei);

        if (payload && payload.data && !payload.data.telephony) {
            payload.data.telephony = telephony;
        }
        else if (payload && payload.data && payload.data.telephony) {
            payload.data.telephony.imei = telephony.imei;

            if (telephony.tac) {
                payload.data.telephony.tac = telephony.tac;
            }
        } else if (!payload.data) {
            payload.data = {
                telephony: telephony
            };
        }

        return payload;
    };

    /**
     * Returns whether the imei check is turned on or not.
     * @returns {boolean}
     */
    var isImeiEnabled = function() {
        return !!(DeviceValidation.options && (DeviceValidation.options.imei || DeviceValidation.options.imeiEnabled)) || false;
    };

    /**
     * Builds the payload for the API request
     * @param licence
     * @param imei
     * @returns {{data: {memory: {}, sensors: [], build: {brand: *}, usb: {}, web: {webview: {jsProps: {}}, webviewUserAgent, systemUserAgent}, telephony: {isVoiceCapable: *, tac: null, imei: null, phoneCount: number}, display: {xDpi: *, heightPixels: number, yDpi: *, widthPixels: number}, cpu: {numCores: *, availableProcessors: *}, storage: {}, camera: [], gpu: {}}, info: {date: string, dataCollectorKey: *, serverToServer: boolean, source: string, version: string}}}
     */
    var buildPayload = function(licence, imei) {
        var gpuProperties = getGpuProperties();

        var payload = {
            info: {
                version: version,
                date: now(),
                source: 'Device Validation Web Client',
                serverToServer: false
            },
            data: {
                build: {
                    brand: navigator.platform,
                },
                cpu: {
                    availableProcessors: navigator.hardwareConcurrency,
                    numCores: navigator.hardwareConcurrency
                },
                memory: {
                },
                display: {
                    widthPixels:  window.screen.width,
                    heightPixels: window.screen.height,
                    xDpi: window.screen.deviceXDPI,
                    yDpi: window.screen.deviceYDPI,
                },
                telephony: _buildTelephony(imei),
                storage: {},
                usb: {},
                camera: [],
                sensors: [],
                web: {
                    systemUserAgent: (navigator||{}).userAgent,
                    webviewUserAgent:(navigator||{}).userAgent,
                    webview: {
                        jsProps: {}
                    }
                },
                gpu: {}
            }
        };

        // Add errors carried over to the payload
        payload.errors = prevErrors || [];

        // for (var key in (missingProperties || {})) {
        //     var attempts = missingProperties[key];
        //
        //     if (attempts && attempts.length > 0) {
        //         payload.errors.push({ missingProperty: key, attempts: attempts });
        //     }
        // }

        if (gpuProperties.vendor) {
            payload.data.gpu.vendor = gpuProperties.vendor;
        }

        if (gpuProperties.renderer) {
            payload.data.gpu.renderer = gpuProperties.renderer;
        }

        // use the concatenated String form of properties from the DeviceAtlas client-side
        // component until such time DeviceAtlas adds an easier way to get all the properties.

        var deviceAtlasProperties = getDeviceAtlasProperties();
        payload.data.web.webview.jsProps = getAdditionalWebProperties(deviceAtlasProperties);

        payload.data.web.webview.clientSide = getDevicePropsAsString();

        if (!_isCustomUrlMode()) {
            payload.info.dataCollectorKey = licence;
        }

        __flushCache();

        return payload;
    };

    /**
     * Converts the given string value to the specified type. Converts to Integer
     * and Boolean types. String types are just passed through.
     */
    var convertType = function(type, val) {
        switch(type) {
            case 'b':
                val = (val === '1' || val.toLowerCase() === 'true');
                break;
            case 'i':
                val = parseInt(val);
                break;
        }
        return val;
    };

    var failover = function(licence, onSuccess, onError, payload, response, reject, urls) {

        if (isLastRetryAttempt(urls)) {
            attempt = 0;
            flushErrors();
            onError(response);

            if (reject) {
                return reject(response);
            }
            return response;
        }
        else {
            attempt++;
            var url = (urls || getApiUrls())[attempt];

            if (isPromiseSupported()) {

                return _DeviceValidation.check(licence, onSuccess, onError, payload, url, true, urls)
                    .then(function onFulfilled() {}, reject);

            } else {
                _DeviceValidation.check(licence, onSuccess, onError, payload, url, true, urls);
            }
        }
    };

    var getGpuProperties = function() {

        var props = {};
        var canvas = document.createElement('canvas');
        var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        if (!!gl) {
            var debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

            if (!debugInfo) {
                return props;
            }

            props.vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
            props.renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        }

        return props;
    };

    var isLastRetryAttempt = function(urls) {
        var attemptURLS = getApiUrls();

        if (urls) {
            attemptURLS = urls;
        }

        return attempt === attemptURLS.length - 1;
    };

    var isPromiseSupported = function() {
        if (!Promise || !window.Promise) {
            return false;
        }
        return typeof Promise !== 'undefined' || typeof window.Promise !== 'undefined';
    };

    var now = function() {
        var today = new Date();
        var dd = today.getDate();
        var mm = today.getMonth()+1; //January is 0!
        var yyyy = today.getFullYear();
        var hh = today.getHours();
        var MM = today.getMinutes();
        var ss = today.getSeconds();

        if (dd < 10) {
            dd = '0' + dd;
        }

        if (mm < 10) {
            mm = '0' + mm;
        }

        if (hh < 10) {
            hh = '0' + hh;
        }

        if (MM < 10) {
            MM = '0' + MM;
        }

        if (ss < 10) {
            ss = '0' + ss;
        }

        return yyyy + '-' + mm + '-' + dd + 'T' + hh + ':' + MM + ':' + ss;
    };

    var isMobile = function(userAgent, platform, vendor) {

        var navigatorDetails = (userAgent || platform || vendor || '').toLowerCase();

        var keywordCheck = /(android|bb\d+|meego).+mobile|avantgo|bada\/|armv7|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigatorDetails);
        var patternCheck = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigatorDetails.substr(0, 4));
        var operaMiniCheck = /pike v(\d+)*(\.)*(\d+) release (\d+)*/i.test(navigatorDetails);
        var webOsCheck = /webos\w*/i.test(navigatorDetails);

        return (keywordCheck || patternCheck || operaMiniCheck || webOsCheck);
    };

    /**
     * Returns the storage type for the configuration for Device Validation.
     *
     * Should return whatever is configured, or the default value:
     * - cookie
     * - local-storage
     *
     * @returns {string}
     * @private
     */
    var _getStorageType = function () {

        var storage = (_DeviceValidation.options && _DeviceValidation.options.storageType) || null;

        if ([STORAGE_TYPE.LOCAL_STORAGE, STORAGE_TYPE.COOKIE, STORAGE_TYPE.NONE].indexOf(storage) > -1) {
            return storage;
        }

        return _getDefaultOptions().storageType;
    };

    var _getStorageExpiry = function () {
        var expiry = (_DeviceValidation.options && _DeviceValidation.options.storageExpiry) || null;

        if (expiry) {
            return expiry;
        }

        return _getDefaultOptions().storageExpiry;
    };

    var _getStorageKey = function () {

        var key = (_DeviceValidation.options && _DeviceValidation.options.storageKey) || null;

        if (key) {
            return key;
        }

        return _getDefaultOptions().storageKey;
    };

    var localStorageSupport = function () {
        return (localStorage && !!localStorage.getItem) || false;
    };

    var _getTTL = function (name) {
        var type = _getStorageType();

        _cleanCache();

        if (type === STORAGE_TYPE.NONE) {
            return null;
        }

        var value = null;
        if (type === STORAGE_TYPE.COOKIE) {
            value = _getCookie(name);
        }
        else if (type === STORAGE_TYPE.LOCAL_STORAGE) {
            value = _getLocalStorage(name);
        }

        if (value && typeof value === 'string') {
            return JSON.parse(value);
        }
        else if (value && typeof value === 'object') {
            return value;
        }

        return null;
    };

    var _cleanCache = function () {

        if (_getStorageType() !== STORAGE_TYPE.NONE) {
            return;
        }

        var cacheKey = _getStorageKey();

        if (localStorageSupport()) {
            localStorage.removeItem(cacheKey);
        }

        var defaultCacheValue = JSON.stringify({ value: DEFAULT_OPTIONS.CACHE_VALUE });
        var expiryDate = new Date(0).toUTCString();

        document.cookie = cacheKey + '=' + defaultCacheValue + ';expires=' + expiryDate + ';path=/';
    };

    var _getCacheValue = function () {
        return _getTTL(_getStorageKey());
    };

    var _getCookie = function(name) {

        if (!document.cookie) {
            return null;
        }

        if (_getStorageType() === STORAGE_TYPE.NONE) {
            _cleanCache();
            return null;
        }

        var cookies = document.cookie.split(';');

        for (var i = 0; i < cookies.length; i++) {
            var cookie = cookies[i];
            var cookieParts = cookie.split('=');

            if (cookieParts.length === 2 && !!cookieParts[0] && cookieParts[0].trim() === name) {
                return cookieParts[1];
            }
        }
        return null;
    };

    /**
     * Returns the DV storage value
     *
     * When the item is expired or invalid, a value of '' is returned
     *
     * Otherwise, the value is returned.
     *
     * @param name
     * @returns {string}
     * @private
     */
    var _getLocalStorage = function (name) {
        if (!localStorageSupport()) {
            return '';
        }

        if (_getStorageType() === STORAGE_TYPE.NONE) {
            _cleanCache();
            return '';
        }

        var cachedProperties = window.localStorage.getItem(name);
        if (!cachedProperties) {
            return '';
        }

        var item = '';

        try {
            item = JSON.parse(cachedProperties);
        } catch(e) {
            localStorage.removeItem(name);
            return '';
        }
        if ((new Date()).getTime() > item.expiry) {
            localStorage.removeItem(name);
            return '';
        }

        var value = '';

        if (item && item.value !== undefined || item.value !== null) {
            value = item.value;
        }
        return value;
    };

    var _setTTL = function (response) {
        var type = _getStorageType();

        if (type === STORAGE_TYPE.NONE) {
            _cleanCache();
            return;
        }

        if (type === STORAGE_TYPE.COOKIE) {
            _setCookie(response);
        }
        else if (type === STORAGE_TYPE.LOCAL_STORAGE) {
            _setLocalStorage(response);
        }
    };

    var _setCookie = function(value) {

        if (_getStorageType() === STORAGE_TYPE.NONE) {
            _cleanCache();
            return;
        }

        var expiryDate = new Date();
        var cacheKey = _getStorageKey();

        var expiry = _getStorageExpiry();
        if (expiry === undefined || expiry === null) {
            expiry = DEFAULT_OPTIONS.THIRTY_MINUTES_IN_MILLIS;
        }

        expiryDate.setTime(expiryDate.getTime() + expiry);

        var valueToStore = value || { value: DEFAULT_OPTIONS.CACHE_VALUE };
        if (typeof valueToStore === 'object') {
            valueToStore = JSON.stringify(valueToStore);
        }

        if (document.cookie.indexOf(cacheKey + '=' + valueToStore) === -1) {
            document.cookie = cacheKey + '=' + valueToStore + ';expires=' + expiryDate.toUTCString() + ';path=/';
        }

        localStorage.removeItem(cacheKey);
    };

    var _setLocalStorage = function (value) {

        if (_getStorageType() === STORAGE_TYPE.NONE) {
            _cleanCache();
            return;
        }

        var valueToStore = value || { value: DEFAULT_OPTIONS.CACHE_VALUE };
        if (typeof valueToStore === 'object') {
            valueToStore = JSON.stringify(valueToStore);
        }

        var cacheKey = _getStorageKey();

        var expiry = _getStorageExpiry();
        if (expiry === undefined || expiry === null) {
            expiry = DEFAULT_OPTIONS.THIRTY_MINUTES_IN_MILLIS;
        }

        var item = { value: valueToStore, expiry: (new Date().getTime() + expiry) };
        window.localStorage.setItem(cacheKey, JSON.stringify(item));

        var defaultCacheValue = JSON.stringify({ value: DEFAULT_OPTIONS.CACHE_VALUE });
        var expiryDate = new Date(0).toUTCString();

        document.cookie = cacheKey + '=' + defaultCacheValue + ';expires=' + expiryDate + ';path=/';
    };

    /**
     * Default options for the DeviceValidation Library
     * @returns
     * {{
     *   licence: string,
     *   serverToServerEnabled: boolean,
     *   imei: false,: boolean,
     *   customUrlMode: boolean,
     *   onSuccessServerToServer: function,
     *   onSuccessServerToServer: onErrorServerToServer,
     *   onError: function,
     *   onSuccess: function,
     *   urls: string[],
     *   storageType: string,
     * }}
     */
    function _getDefaultOptions() {

        var _DEFAULT_URLS;
        /* production-urls */
        _DEFAULT_URLS = ['https://api1.devicevalidation.io/check', 'https://api2.devicevalidation.io/check', 'https://api3.devicevalidation.io/check'];
        /* end-production-urls */

        return {
            licence: undefined,
            serverToServerEnabled: false,
            imei: false,
            customUrlMode: false,
            onSuccess: undefined,
            onError: undefined,
            urls: _DEFAULT_URLS,
            storageExpiry: DEFAULT_OPTIONS.THIRTY_MINUTES_IN_MILLIS,
            storageKey: DEFAULT_OPTIONS.CACHE_KEY,
            storageType: STORAGE_TYPE.LOCAL_STORAGE // can also be STORAGE_TYPE.COOKIE or STORAGE_TYPE.NONE
        };
    }

    var runCheckAuto = function(options) {
        propertiesLoadComplete = true;

        var dvOptions = options;
        if (!dvOptions) {
            dvOptions = {};
        }

        var serverToServer = isServerToServer(dvOptions);
        var isImei = (dvOptions && (dvOptions.imeiEnabled || dvOptions.imei)) || false;

        if (isValidLicenceKey(dvOptions) && !serverToServer && !isImei) {
            _DeviceValidation.check(dvOptions.licence, dvOptions.onSuccess, dvOptions.onError);
        }
    };

    // Adding additional properties requested from DV-1324
    var getAdditionalWebProperties = function(deviceAtlasProperties) {

        var webJsProps = {};

        // we need to copy these across as we inject in to the deviceAtlasProperties directly otherwise.
        // this then bloats the clientside string that is generated.
        if (deviceAtlasProperties) {
            var deviceAtlasPropertyKeys = Object.keys(deviceAtlasProperties);
            for (var i = 0; i < deviceAtlasPropertyKeys.length; i++) {
                var propKey = deviceAtlasPropertyKeys[i];

                webJsProps[propKey] = deviceAtlasProperties[propKey];
            }
        }

        if (typeof navigator.deviceMemory !== 'undefined') {
            webJsProps["navigator.deviceMemory"] = navigator.deviceMemory;
        }

        if (typeof navigator.oscpu !== 'undefined') {
            webJsProps["navigator.oscpu"] = navigator.oscpu;
        }

        if (typeof navigator.platform !== 'undefined') {
            webJsProps["navigator.platform"] = navigator.platform;
        }

        if (typeof navigator.productSub !== 'undefined') {
            webJsProps["navigator.productSub"] = navigator.productSub;
        }

        if (typeof navigator.webdriver !== 'undefined') {
            webJsProps["navigator.webdriver"] = navigator.webdriver;
        }

        if ('connection' in navigator){
            if (navigator.connection.saveData) {
                webJsProps["navigator.saveData"] = navigator.connection.saveData;
            }
        }

        if (typeof window.screen.width !== 'undefined') {
            webJsProps["screen.width"] = window.screen.width;
        }
        if (typeof window.screen.height !== 'undefined') {
            webJsProps["screen.height"] = window.screen.height;
        }

        if (typeof window.screen.availWidth !== 'undefined') {
            webJsProps["screen.availWidth"] = window.screen.availWidth;
        }

        if (typeof window.screen.availHeight !== 'undefined') {
            webJsProps["screen.availHeight"] = window.screen.availHeight;
        }

        if (typeof window.ApplePaySession !== 'undefined') {
            webJsProps["applePaySession"] = !!window.ApplePaySession;
        }

        var hasVibrationApi = false;
        if (typeof navigator.vibrate === 'function') {
            hasVibrationApi = !!navigator.vibrate;
        }
        webJsProps['eval.length'] = eval.toString().length;
        webJsProps['hasVibrationApi'] = hasVibrationApi;
        webJsProps['promises'] = isPromiseSupported();

        webJsProps = detectPropertiesForiOS(webJsProps);
        _collectBraveData(webJsProps);
        _collectOperaData(webJsProps);
        _collectSafariData(webJsProps);
        _collectLanguage(webJsProps);
        _collectLanguages(webJsProps);
        _collectOrientationType(webJsProps);

        _collectNavigatorVendor(webJsProps);
        _collectNavigatorVendorSub(webJsProps);
        _collectNavigatorPlugins(webJsProps);
        _collectPluginsPrototypes(webJsProps);
        _collectDimensions(webJsProps);

        _detectAutomation(webJsProps);

        return _addApiData(webJsProps);
    };

    /**
     * Adds the api data collected to the webJsProps passed in.
     */
    var _addApiData = function (webJsProps) {
        if (!apiData) {
            return webJsProps || {};
        }

        var jsApiKeys = Object.keys(apiData);
        if (!jsApiKeys || jsApiKeys.length < 1) {
            return webJsProps || {};
        }

        for (var i = 0; i < jsApiKeys.length; i++) {
            var api = jsApiKeys[i];

            if (!!api) {
                webJsProps[api] = apiData[api];
            }
        }

        return webJsProps || {};
    };

    /**
     * Detects the presence of any phantom keys
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectPhantomKeys = function () {
        var _phantomKeys = ['phantom', 'Phantom'];

        return _detectKeys(_phantomKeys);
    };

    /**
     * Detects the presence of any rhino keys
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectRhinoKeys = function () {
      var _rhinoKeys = ['rhino', 'spawn'];

      return _detectKeys(_rhinoKeys);
    };

    /**
     * Detects the presence of any rhino keys
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectCouchJS = function () {
      var _couchKeys = ['couch', 'emit'];

      return _detectKeys(_couchKeys);
    };

    /**
     * Detects the presence of any nodejs keys
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectBufferKeys = function () {
      var _nodeJsKeys = ['Buffer'];

      return _detectKeys(_nodeJsKeys);
    };

    /**
     * Detects the presence of any domAutomation keys. Used by BrowserStack and the like
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectDomAutomationKeys = function () {
      var _domKeys = ['domAutomation'];

      return _detectKeys(_domKeys);
    };


  /**
     * Returns whether the plugins key is present in the navigator object
     *
     * @returns {boolean}
     */
    var _isNavigatorPluginsAvailable = function () {
      return 'plugins' in navigator || typeof navigator.plugins !== 'undefined';
    };

    /**
     * Collects the dimensions for the window object.
     * When the outerWidth and outerHeight are 0, then it could be a sign of headless
     * @param webJsProps
     * @returns {*}
     * @private
     */
    var _collectDimensions = function (webJsProps) {

      var keys = ['outerWidth', 'outerHeight', 'innerWidth', 'innerHeight'];

      for (var i = 0; i < keys.length; i++) {
        var _result = { a: false, v: [], e: false };

        try {
          var _key = keys[i];
          _result.a = _key in window;
          if (_result.a) {
            _result.v = window[keys[i]];
          }

          webJsProps['window_' + _key] = _result;
        }
        catch (e) {
          _result.e = true;
        }
      }

      if (webJsProps.window_outerWidth && webJsProps.window_outerWidth.a && webJsProps.window_outerWidth && webJsProps.window_outerWidth.a) {
        webJsProps.window_hidden = webJsProps.window_outerWidth.v === 0 && webJsProps.window_outerHeight.v === 0;
      }

      return webJsProps;
    };

    /**
     * Collects all the navigator plugins if they are available
     * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/plugins
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _collectNavigatorPlugins = function (webJsProps) {
      var _result = {
        a: _isNavigatorPluginsAvailable(),
        v: [],
        e: false
      };

      try {

        if (_result.a) {
          for (var i = 0; i < navigator.plugins.length; i++) {
            var plugin = navigator.plugins[i];
            var base64 = btoa(plugin.name + '__' + plugin.filename + '__' + plugin.description);
            _result.v.push(base64);
          }
        }
      }
      catch (e) {
        _result.e = true;
      }

      var _data = webJsProps;
      if (!_data) {
        _data = {};
      }

      _data['navigator_plugins'] = _result;

      return _data;
    };

    /**
     * Collects the navigator vendor property
     * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/vendor
     * @param webJsProps
     * @returns {*}
     * @private
     */
    var _collectNavigatorVendor = function (webJsProps) {
      var _result = { a: false, v: null, e: false };

      try {
        _result.v = navigator.vendor;
        _result.a = typeof navigator.vendorSub !== 'undefined';
      }
      catch (e) {
        _result.e = true;
      }

      var _data = webJsProps;
      if (!_data) {
        _data = {};
      }

      _data['navigator_vendor'] = _result;

      return _data;
    };

    /**
     * Collects the navigator vendorSub property
     * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/vendorSub
     * @param webJsProps
     * @returns {*}
     * @private
     */
    var _collectNavigatorVendorSub = function (webJsProps) {
      var _result = { a: false, v: null, e: false };

      try {
        _result.v = navigator.vendorSub;
        _result.a = typeof navigator.vendorSub !== 'undefined';
      }
      catch (e) {
        _result.e = true;
      }

      var _data = webJsProps;
      if (!_data) {
        _data = {};
      }

      _data['navigator_vendorSub'] = _result;

      return _data;
    };

    /**
     * Collects the navigator plugins prototype
     * https://github.com/infosimples/detect-headless/blob/master/scripts/detect_headless.js
     * When this is false, its a headless browser
     * @param webJsProps
     * @returns {*}
     * @private
     */
    var _collectPluginsPrototypes = function (webJsProps) {
      var _result = { a: Object && Object.getPrototypeOf !== 'undefined', v: null, e: false };

      if (!_isNavigatorPluginsAvailable()) {
        return _result;
      }

      try {
        _result.v = PluginArray.prototype ===  Object.getPrototypeOf(navigator.plugins);
        if (navigator.plugins.length > 0) {
          _result.v = _result.v && Plugin.prototype === Object.getPrototypeOf(navigator.plugins[0]);
        }
      }
      catch (e) {
        _result.e = true;
      }

      var _data = webJsProps;
      if (!_data) {
        _data = {};
      }

      _data['navigator_plugins_prototype'] = _result;

      return _data;
    };

    /**
     * Hook method to collect all the automation framework data
     * @param webJsProps
     * @private
     */
    var _detectAutomation = function (webJsProps) {
      var automation = {
        phantom: _detectPhantomKeys(),
        rhino: _detectRhinoKeys(),
        buffer: _detectBufferKeys(),
        domAutomation: _detectDomAutomationKeys(),
        couch: _detectCouchJS(),
        cdc: _detectChromeDriver(),
        karma: _detectKarmaKeys(),

        window_nightmare: _detectNightmareInWindow(),
        document_nightmare: _detectNightmareInDocument(),
        window_selenium: _detectSeleniumInWindow(),
        document_selenium: _detectSeleniumInDocument(),
        window_cypress: _detectCypressInWindow(),
        document_cypress: _detectCypressInDocument(),

        document: _detectDocumentKeys()
      };

      var _data = webJsProps;
      if (!_data) {
        _data = {};
      }

      try {
        var automationKeys = Object.keys(automation);

        for (var i = 0; i < automationKeys.length; i++) {
          var _key = automationKeys[i];
          if (automation[_key].a) {
            _data[_key] = automation[_key];
          }
        }
      }
      catch (e) {}

      return _data;
    };

    /**
     * Detects the presence of any Karma keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectKarmaKeys = function () {
      var _karmaKeys = ['karma', 'Karma'];

      return _detectKeys(_karmaKeys);
    };

    /**
     * Detects the presence of any Test keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectTestKeys = function () {
      var _testKeys = ['test', 'Test'];

      return _detectKeys(_testKeys);
    };

    /**
     * Detects the presence of any Chrome Driver keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectChromeDriver = function () {
      var chromeDriverKeys = ['cdc'];

      return _detectKeys(chromeDriverKeys);
    };

    /**
     * Detects the presence of any Nightmare keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectNightmareInWindow = function () {
        var nightmareKeys = ['nightmare', 'Nightmare'];

        return _detectKeys(nightmareKeys);
    };

    /**
     * Detects the presence of any Nightmare keys in the document object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectNightmareInDocument = function () {
        var nightmareKeys = ['nightmare', 'Nightmare'];

        return _detectKeys(nightmareKeys, 'document');
    };


    /**
     * Detects the presence of any Cypress keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectCypressInWindow = function () {
        var keys = ['cypress', 'Cypress'];

        return _detectKeys(keys);
    };


    /**
     * Detects the presence of any Cypress keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectCypressInDocument = function () {
        var keys = ['cypress', 'Cypress'];

        return _detectKeys(keys, 'document');
    };

    /**
     * Detects the presence of any Selenium keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectSeleniumInWindow = function () {
        var keys = ['selenium', 'Selenium'];

        return _detectKeys(keys);
    };

    /**
     * Detects the presence of any Selenium keys in the window object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectSeleniumInDocument = function () {
        var keys = ['selenium', 'Selenium'];

        return _detectKeys(keys, 'document');
    };

    /**
     * Detects the presence of any Document keys in the document object
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectDocumentKeys = function () {
        var keys = [
          'func',
          'fn',
          'evaluate',
          'unwrapped',
          'driver',
          'fx',
          '_'
        ];

        return _detectKeys(keys, 'document');
    };


    /**
     * Detects the presence of any keys in the object passed in
     * @param detections
     * @param type
     * @returns {{a: boolean, e: boolean, v: *[]}}
     * @private
     */
    var _detectKeys = function (detections, type) {
      var _result = {
        a: false,
        v: [],
        e: false
      };

      var foundKeys = {};
      if (!type) {
          type = 'window';
      }
      var _keys = [];

      if (type === 'window') {
          _keys = _getWindowKeys();
      }
      else if (type === 'document') {
          _keys = _getDocumentKeys();
      }
      else if (type === 'navigator') {
          _keys = _getNavigatorKeys();
      }

      for (var i = 0; i < _keys.length; i++) {

        if (!_keys[i]) {
            continue;
        }

        var key = _keys[i];

        for (var j = 0; j < detections.length; j++) {
          try {
            var detection = detections[j];
            if (key.indexOf(detection) > -1) {
              foundKeys[key] = i;
            }
          }
          catch (e) {
            _result.e = true;
          }
        }
      }
      _result.v = Object.keys(foundKeys);

      if (_result.v.length > 0) {
        _result.a = true;
      }

      return _result;
    };

    /**
     * Returns a list of keys within the window object
     * @param flush
     * @returns {*|*[]}
     * @private
     */
    var _getWindowKeys = function (flush) {
      try {
        return __getCacheKey(flush,'window', window);
      }
      catch (e) {
        return [];
      }
    };

    /**
     * Returns a list of keys within the document object
     * @param flush
     * @returns {*|*[]}
     * @private
     */
    var _getDocumentKeys = function (flush) {
        try {
            return __getCacheKey(flush,'document', document);
        }
        catch (e) {
            return [];
        }
    };

    /**
     * Returns a list of keys within the navigator object
     * @param flush
     * @returns {*|*[]}
     * @private
     */
    var _getNavigatorKeys = function (flush) {
        try {
          return __getCacheKey(flush,'navigator', navigator);
        }
        catch (e) {
          return [];
        }
    };

    /**
     * Gets a list of keys within the object passed in and stores that list in the cache.
     * @param flush
     * @param key
     * @param obj
     * @returns {*}
     * @private
     */
    var __getCacheKey = function (flush, key, obj) {
      if (flush && !!_cache[key]) {
        delete _cache[key];
      }

      if (!_cache[key]) {
        _cache[key] = Object.keys(obj);
      }

      return _cache[key];
    };

    var __flushCache = function () {
      if (_cache) {

        try {
          var _cacheKeys = Object.keys(_cache);
          for (var i = 0; i < _cacheKeys.length; i++) {
            delete _cache[_cacheKeys[i]];
          }

          _cache = {};
        }
        catch(e) {}
      }
    };

    /**
     * Attempts to collect the battery manager information
     */
    var _detectBatteryManager = function () {

        if (!apiData) {
            apiData = {};
        }

        var isPresent = !!navigator.getBattery && typeof navigator.getBattery === 'function';

        apiData.getBattery = { available: isPresent };

        if (isPresent) {
            var onSuccess = function (battery) {
                apiData.getBattery.charging = battery.charging;
                apiData.getBattery.level = battery.level;
                apiData.getBattery.chargingTime = battery.chargingTime;
                apiData.getBattery.dischargingTime = battery.dischargingTime;
            };

            var onError = function () {
                apiData.getBattery.failed = true;
            };

            navigator.getBattery()
                .then(onSuccess)
                .catch(onError);
        }

        return apiData;
    };

    /**
     * Adds sensor object to the jsProps passed in.
     */
    var _detectWebApiPermissions = function () {

        if (!apiData) {
            apiData = {};
        }

        // DeviceMotion and DeviceOrientation events should be true in iOS
        // IdleDetector events should be true in chrome [desktop]
        var apis = [
            'DeviceMotionEvent',
            'DeviceOrientationEvent',
            'DeviceMotionEventAcceleration',
            'DeviceMotionEventRotationRate',

            'IdleDetector',

            'DragEvent',
            'GestureEvent' // Looks to be Apple only
        ];

        for (var i = 0; i < apis.length; i++) {
            var apiName = apis[i];

            if (!apiName){
                continue;
            }

            apiData[apiName] = {
                available: !!window[apiName],
                requiresPermission: _apiRequiresPermissions(apiName)
            };
        }
        return apiData;
    };

    /**
     * Looks at the Permissions API and records the state of each permission.
     */
    var _detectPermissionsApi = function () {

        var isAvailable = navigator && !!navigator.permissions;
        var isQueryPresent = isAvailable && !!navigator.permissions.query && typeof navigator.permissions.query === 'function';

        permissions = {
            available: isAvailable,
            query: isQueryPresent,
            inComplete: [],
            completed: {},
            failed: 0
        };

        if (!apiData) {
            apiData = {};
        }
        apiData.permissions = permissions;

        if (isQueryPresent) {
            var recordFailedAttempt = function () {
                var failed = permissions.failed;
                if (!failed) {
                    permissions.failed = 0;
                }
                permissions.failed++;
            };

            var onSuccess = function (result) {
                if (!result || !result.name || !result.state) {
                    recordFailedAttempt();
                    return;
                }
                var name = result.name;
                var index = permissions.inComplete.indexOf(name);
                permissions.inComplete.splice(index, 1);

                permissions.completed[name] = {
                    state: result.state,
                    onChange: !!result.onchange
                };
                return apiData;
            };

            for (var i = 0; i < _PERMISSIONS.length; i++) {
                var perm = _PERMISSIONS[i];
                permissions.inComplete.push(perm);

                navigator.permissions.query({ name: perm })
                    .then(onSuccess)
                    .catch(recordFailedAttempt);
            }
        }
        return apiData;
    };

    /**
     * Records if the specified api requires permissions to run a sensor.
     */
    var _apiRequiresPermissions = function (apiName) {
        if (!apiName) {
            return false;
        }

        var requestPermission = (!!window[apiName] && window[apiName].requestPermission) || null;

        return typeof requestPermission === 'function';
    };

    var _collectData = function (dataFunction) {
      var data = {
        a: false,
        e: false
      };

      try {
        var result = dataFunction();
        if (result) {
          data.a = true;
          data.v = result;
        }
      }
      catch (e) {
        data.e = true;
      }

      return data;
    };

    /**
     * Attempts to collect the language information
     * @param webJsProps
     */
    var _collectLanguage = function (webJsProps) {
      var language_data = _collectData(function () {
        return navigator.language;
      });

      webJsProps['navigator_language'] = language_data;
    };

    /**
     * Attempts to collect the languages information
     * @param webJsProps
     */
    var _collectLanguages = function (webJsProps) {
      var languages_data = _collectData(function () {
        return navigator.languages;
      });

      webJsProps['navigator_languages'] = languages_data;
    };

    /**
     * Attempts to collect the orientation type
     * @param webJsProps
     */
    var _collectOrientationType = function (webJsProps) {
        var orientation_data = _collectData(function () {
          return window.screen.orientation.type;
        });

        webJsProps['screen_orientation_type'] = orientation_data;
    };

    /**
     * Attempts to identify if the brave properties are present.
     * @param {At} webJsProps
     * @returns
     */
    var _collectBraveData = function (webJsProps) {

        webJsProps.brave = {
            available: false
        };

        if (!navigator.brave || typeof navigator.brave.isBrave !== 'function') {
            return;
        }

        webJsProps.brave.available = true;
    };

    var _collectOperaData = function (webJsProps) {

        webJsProps.opera = {
            available: false
        };

        if (window.opr) {
            webJsProps.opera.available = true;
        }
    };

    var _collectSafariData = function (webJsProps) {

        webJsProps.safari = {
            available: false
        };

        if (!window.safari || typeof safari.pushNotification !== 'function') {
            return;
        }

        webJsProps.safari.available = true;
    };

    var detectPropertiesForiOS = function (webJsProps) {

        var jsProps = webJsProps;
        if (!jsProps) {
            jsProps = {};
        }

        // iPhone simulator does not have these properties yet.
        jsProps['document.pictureInPictureElement'] = document.pictureInPictureElement ? true : null;


        var pictureInPictureEnabled = false;
        if (document.pictureInPictureEnabled || document.pictureInPictureEnabled === false) {
            pictureInPictureEnabled = document.pictureInPictureEnabled;
        }

        jsProps['document.pictureInPictureEnabled'] = pictureInPictureEnabled;

        // iPads and Browsers have these properties.
        jsProps['document.webkitCancelFullScreen'] = document.webkitCancelFullScreen ? true : null;
        jsProps['document.webkitExitFullScreen'] = document.webkitExitFullScreen ? true : null;
        jsProps['document.webkitCurrentFullScreenElement'] = document.webkitCurrentFullScreenElement ? true : null;
        jsProps['document.webkitFullscreenElement'] = document.webkitFullscreenElement ? true : null;

        var webkitFullscreenEnabled = false;
        if (document.webkitFullscreenEnabled || document.webkitFullscreenEnabled === false) {
            webkitFullscreenEnabled = document.webkitFullscreenEnabled;
        }
        jsProps['document.webkitFullscreenEnabled'] = webkitFullscreenEnabled;

        var webkitFullScreenKeyboardInputAllowed = false;
        if (document.webkitFullScreenKeyboardInputAllowed || document.webkitFullScreenKeyboardInputAllowed === false) {
            webkitFullScreenKeyboardInputAllowed = document.webkitFullScreenKeyboardInputAllowed;
        }
        jsProps['document.webkitFullScreenKeyboardInputAllowed'] = webkitFullScreenKeyboardInputAllowed;

        // iOS property
        jsProps['navigator.standalone'] = navigator.standalone === null || navigator.standalone === null ? null : navigator.standalone;

        return jsProps;
    };

    var isMediaDevicesAvailable = function() {
        return typeof navigator.mediaDevices !== 'undefined';
    };

    var initIsAppleDevice = function() {
        if (isAppleDevice !== undefined) {
            return;
        }
        isAppleDevice = !!navigator.userAgent && /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
    };

    var getDevicePropsAsString = function() {

        devicePropertiesAsString = (_getDeviceAtlasCollector().getPropertiesAsString && _getDeviceAtlasCollector().getPropertiesAsString()) || '';

        if (typeof devicePropertiesAsString !== 'string') {
            devicePropertiesAsString = '';
        }

        return devicePropertiesAsString;
    };

    var getDeviceAtlasProperties = function () {
        var properties = (_getDeviceAtlasCollector().getProperties && _getDeviceAtlasCollector().getProperties()) || {};

        if (typeof properties !== 'object') {
            properties = {};
        }

        return properties;
    };


    function _createMissingPropertyAttempt(attempt, type) {

        if (!type) {
            type = MESSAGE_TYPES.WAIT_AUDIO_REF;
        }

        return {
            isFirst: attempt === 1,
            date: Date.now(),
            attempt: attempt,
            present: false,
            type: type
        };
    }

    /**
     * Function is triggered onLoad of the DeviceAtlas library and when we detect any changes.
     * @param properties
     */
    function _onPropertiesUpdate(properties, propertiesAsString) {
        _collect(MESSAGE_TYPES.DA_MESSAGE, true);
    }

    /**
     * Checks whether the daProps string passed in has the audioRef in it or not.
     * @param daProps
     * @returns {boolean}
     * @private
     */
    function _hasAudioRef() {
        var daProps = getDeviceAtlasProperties();

        var audioRef = (daProps && daProps.audioRef) || null;

        // common scenarios
        if ([null, 'null', '', 0].indexOf(audioRef) > -1) {
            return false;
        }

        return !!((audioRef + '').trim()); // check if present
    }

    /***
     * To tackle a situation where it is an Apple device but never gives back the audioRef then
     * wait for 1 second and continue with execution of the script.
     *
     * @param timeToWait
     * @param doRequest
     * @param type
     */
    function _asyncCheckForApple(timeToWait, doRequest, type) {

        if (type && type !== MESSAGE_TYPES.DA_MESSAGE && (!isAppleDevice || timeoutInitialized || hasExceededAttempts('AUDIO_REF'))) {
            return;
        }

        timeoutInitialized = true;
        WAIT_ATTEMPTS.AUDIO_REF++;

        if (!timeToWait || timeToWait < 1) {
            timeToWait = DEFAULT_TIME_TO_WAIT;
        }

        var asyncCheck = function() {
            timeoutInitialized = false;

            if (!isAppleDevice) {
                return;
            }

            var audioRefProperties = !!missingProperties.audioRef ? missingProperties.audioRef : [];
            var attempt = _createMissingPropertyAttempt(audioRefProperties.length + 1, MESSAGE_TYPES.WAIT_AUDIO_REF);

            // Flag that our audioRef was missing or present for this attempt.
            attempt.present = _hasAudioRef();
            audioRefProperties.push(attempt);

            missingProperties.audioRef = audioRefProperties;

            var exceeded = hasExceededAttempts('AUDIO_REF');

            // Attempt to find the audioRef at most 10 times, total time to take should be between 100ms to 300ms
            if (!attempt.present && !exceeded) {
                _asyncCheckForApple(timeToWait, doRequest, MESSAGE_TYPES.WAIT_AUDIO_REF);
            }
            else if (doRequest || exceeded) {
                runCheckAuto(_DeviceValidation.options, type);
            }
        };

        setTimeout(asyncCheck, timeToWait);
    }

    /***
     * Triggers the collecting of the device data using the DA library, when doRequest is true, it will
     * perform the check call.
     *
     * @param properties
     * @param type
     * @param doRequest
     */
    function _collect(type, doRequest) {
        initIsAppleDevice();

        if (!type) {
            type = MESSAGE_TYPES.ON_LOAD;
        }

        if ((type === MESSAGE_TYPES.ON_LOAD || !doRequest) && isServerToServer(_DeviceValidation.options)) {
            // don't do any collection for server to server for the audioRef. It is done onPropertyUpdate
            return;
        }

        var noWait;

        // no audio ref, record we're missing it.
        if (isAppleDevice) {
            var audioRefProperties = !!missingProperties.audioRef ? missingProperties.audioRef : [];
            var attempt = _createMissingPropertyAttempt(audioRefProperties.length + 1, type);
            attempt.present = _hasAudioRef();

            // Flag that our audioRef was missing for this attempt.
            audioRefProperties.push(attempt);
            missingProperties.audioRef = audioRefProperties;

            noWait = attempt.present;
        } else {
            noWait = doRequest && (!isAppleDevice || _hasAudioRef());
        }

        if (noWait) {
            runCheckAuto(_DeviceValidation.options);
        } else {
            _asyncCheckForApple(DEFAULT_TIME_TO_WAIT, doRequest, type);
        }
    }

    /**
     * Initialises collection of api data, this will be added to the jsProps when buildPayload is up.
     *
     * @private
     */
    function _detectApis() {
        if (apiData !== null) {
            return;
        }
        apiData = {};

        _detectPermissionsApi();
        _detectBatteryManager();
        _detectWebApiPermissions();
    }

    /**
     * Initialises the DeviceValidation configurations
     *
     * @private
     */
    function _initDV(DV) {
        var overridingOptions = (DeviceValidation && DeviceValidation.options) || null;

        if (!overridingOptions) {
            return;
        }

        var options = _getDefaultOptions();

        if (overridingOptions.serverToServer === true) {
            options.serverToServerEnabled = overridingOptions.serverToServer;


            if (overridingOptions.onSuccessServerToServer) {
                options.onSuccessServerToServer = overridingOptions.onSuccessServerToServer;
            }
            if (overridingOptions.onErrorServerToServer) {
                options.onErrorServerToServer = overridingOptions.onErrorServerToServer;
            }
        }

        if (!!overridingOptions.licence) {
            options.licence = overridingOptions.licence;
        }

        if (overridingOptions.imei === true) {
            options.imeiEnabled = overridingOptions.imei;
        }

        if (overridingOptions.urls && overridingOptions.urls.length > 0) {
            options.urls = overridingOptions.urls;
            options.customUrlMode = true;
            options.licence = undefined;
        }

        if (overridingOptions.onSuccess) {
            options.onSuccess = overridingOptions.onSuccess;
        }

        if (overridingOptions.onError) {
            options.onError = overridingOptions.onError;
        }

        if ([STORAGE_TYPE.COOKIE, STORAGE_TYPE.LOCAL_STORAGE, STORAGE_TYPE.NONE].indexOf(overridingOptions.storageType) > -1) {
            options.storageType = overridingOptions.storageType;
        }

        if (options.storageType !== STORAGE_TYPE.NONE && overridingOptions.storageExpiry &&
            overridingOptions.storageExpiry > 0 && overridingOptions.storageExpiry <= DEFAULT_OPTIONS.TWENTY_FOUR_HOURS_IN_MILLIS) {

            options.storageExpiry = overridingOptions.storageExpiry;
        }

        if (!!overridingOptions.licence && overridingOptions.urls && overridingOptions.urls.length > 0) {
            throw new Error('Licence key cannot be used in conjunction with custom urls');
        }

        DV.options = options;

        return DV;
    }

    function _isCustomUrlMode() {
        if (!_DeviceValidation.options) {
            return false;
        }
        return (!_DeviceValidation.options.licence && _DeviceValidation.options.customUrlMode) || false;
    }

    function _getDeviceAtlasCollector() {
        if (!_DeviceAtlasCollector) {
            _DeviceAtlasCollector = {};
        }

        return _DeviceAtlasCollector;
    }

    /**
     * Initialisation for the DeviceAtlas & DeviceAssure web library.
     *
     * If we don't have a cookie or our cookie has expired, we will start to collect the DeviceAtlas properties in the
     * background.
     *
     * If we have a cookie that has not expired, we make no attempt to initialise the DeviceAtlas library.
     *
     * If server to server configs are enabled, then we override whatever cookie we have and collect the data.
     *
     * The collection of the data is done by not adding a DeviceAtlas object in to the window.
     *
     * @returns {{}|{cacheType: string, onPropertiesUpdate: _onPropertiesUpdate}}
     */
    function _initDA() {

        _initDV(_DeviceValidation);

        // Library is now loaded after this point as the DA library will look for window.DeviceAtlasCollector.

        var _DeviceAtlasConfig = {
            onPropertiesUpdate: _onPropertiesUpdate,
            cacheType: 'none', // This means we do not cache the DA result.
            waitMilliSecondsToBulkSendProperties: 1
        };

        window.DeviceAtlasCollector = _DeviceAtlasConfig;

        // var DeviceAtlasCollector = is imported here so the DeviceAtlasCollector will be in this scope.

        // grunt-includes
/* Begin: lib/deviceatlas.min.js */
/*
 * Copyright 2024 DeviceAtlas Limited. All rights reserved.
 * http://deviceatlas.com
 */
var DeviceAtlasCollector=function(e){var t={};function n(r){if(t[r])return t[r].exports;var o=t[r]={i:r,l:!1,exports:{}};return e[r].call(o.exports,o,o.exports,n),o.l=!0,o.exports}return n.m=e,n.c=t,n.d=function(e,t,r){n.o(e,t)||Object.defineProperty(e,t,{enumerable:!0,get:r})},n.r=function(e){"undefined"!=typeof Symbol&&Symbol.toStringTag&&Object.defineProperty(e,Symbol.toStringTag,{value:"Module"}),Object.defineProperty(e,"__esModule",{value:!0})},n.t=function(e,t){if(1&t&&(e=n(e)),8&t)return e;if(4&t&&"object"==typeof e&&e&&e.__esModule)return e;var r=Object.create(null);if(n.r(r),Object.defineProperty(r,"default",{enumerable:!0,value:e}),2&t&&"string"!=typeof e)for(var o in e)n.d(r,o,function(t){return e[t]}.bind(null,o));return r},n.n=function(e){var t=e&&e.__esModule?function(){return e.default}:function(){return e};return n.d(t,"a",t),t},n.o=function(e,t){return Object.prototype.hasOwnProperty.call(e,t)},n.p="",n(n.s=2)}([function(e,t,n){"use strict";n.r(t);var r=function(e){return"string"==typeof e&&e&&(e=function(e){return e.replace(/"/g,'\\"')}(e=function(e){return e.replace(/\\/,"\\\\")}(e))),e},o=function(e){if(void 0!==(t=e)&&Array.isArray(t))return function(e){return e.map((function(e){return'"'+r(e.brand)+'";v="'+e.version+'"'})).join(", ")}(e);var t};function i(e){return void 0!==e&&""!==e?'"'+r(e)+'"':e}var c=new Promise((function(e){var t=navigator&&navigator.userAgentData;if(t)try{t.getHighEntropyValues(["architecture","bitness","brands","mobile","model","platform","platformVersion","uaFullVersion,","fullVersionList"]).then((function(t){var n={platformArchitecture:t.architecture,requestingMobileUx:t.mobile,"ch.model":i(t.model),"ch.platform":i(t.platform),"ch.browserList":o(t.brands),"ch.browserFullVersionList":o(t.fullVersionList),"ch.platformVersion":i(t.platformVersion),"ch.bitness":t.bitness},r={};for(var c in n)r[c]=encodeURIComponent(n[c]);e(r)})).catch((function(n){return e({requestingMobileUx:t.mobile,"ch.platform":i(t.platform),"ch.browserList":o(t.brands)})}))}catch(n){e({requestingMobileUx:t.mobile,"ch.platform":i(t.platform),"ch.browserList":o(t.brands)})}else e({})}));t.default=c},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!window.addEventListener}},function(e,t,n){"use strict";n.r(t),n.d(t,"cacheProperty",(function(){return M}));var r=n(7),o=n.n(r),i=n(9),c=n(13),u="._.".length,a=function e(t,n,r){for(var o in void 0===n&&(n=!1),void 0===r&&(r={}),t){var i=n?n+"."+o:o,c=t[o],u=typeof t[o];"object"!==u||Array.isArray(c)?"function"!==u&&"undefined"!==u&&(r[i]=c):e(c,i,r)}return r},s=function(e,t,o){void 0===t&&(t=function(e){void 0===e&&(e=["audioRef","ch.bitness","ch.browserFullVersionList","ch.browserList","ch.model","ch.platform","ch.platformVersion","connectionType","cookieSupport","css.animations","css.columns","css.transforms","css.transitions","deviceAspectRatio","devicePixelRatio","displayColorDepth","downlink","effectiveConnectionType","flashCapable","html.audio","html.canvas","html.inlinesvg","html.svg","html.video.ap4x","html.video.av1","html.video","js.accessDom","js.applicationCache","js.battery","js.deviceMotion","js.deviceOrientation","js.forceTouch","js.geoLocation","js.indexedDB","js.json","js.localStorage","js.modifyCss","js.modifyDom","js.querySelector","js.sessionStorage","js.supportBasicJavaScript","js.supportConsoleLog","js.supportEventListener","js.supportEvents","js.touchEvents","js.webGl","js.webGlRenderer","js.webSockets","js.webSqlDatabase","js.webWorkers","js.xhr","mouseActivity","orientation","platformArchitecture","rendererRef","requestingMobileUx","roundTripTime","saveData","screenWidthHeight","timeZone","userMedia"]);var t={};e.map((function(e){t[e]=n(15)("./"+e+".js").default}));var o=function(){};return r.criticalProperties.map((function(e){t[e]=t[e]||o})),t}()),void 0===o&&(o=!1);var i=function(n){var r,i=e[n];if(i||!t.hasOwnProperty(n))return 1;var c=t[n],u=c.length<=1;try{r=c(i,(function(e){U.cacheProperty(n,e,i)}),e)}catch(e){U.debug(e)}(o||u)&&U.cacheProperty(n,r,i)};for(var c in t)i(c)},f=function(e,t,n,r,o){var u;switch(e){case"cookie":u=function(e){if(!Object(i.default)())return"";for(var t=e+"=",n=document.cookie.split(";"),r=0;r<n.length;r++){for(var o=n[r];" "===o.charAt(0);)o=o.substring(1);if(0===o.indexOf(t)){var c=o.substring(t.length,o.length);'"'===c.charAt(0)&&(c=c.substr(1));var u=c.length-1;return'"'===c[u]&&(c=c.substring(0,u)),c}}return""}(r);break;case"local-storage":u=function(e){if(!Object(c.default)())return"";var t=window.localStorage.getItem(e);if(!t)return"";var n="";try{n=JSON.parse(t)}catch(t){return localStorage.removeItem(e),""}return(new Date).getTime()>n.expiry?(localStorage.removeItem(e),""):n.value}(t);break;default:return}return function(e,t){if(!e||""===e)return t;for(var n=e.split("|"),r=0;r<n.length;r++){var o=n[r],i=o.indexOf(":"),c=o.substr(0,1),u=o.substr(1,i-1);if("timeTook"!==u){var a=o.substring(i+1);switch(c){case"i":t[u]=1*a;break;case"b":t[u]=!!(1*a);break;default:t[u]=a}}}return t}(u,o)},l=(new Date).getTime(),d=window.DeviceAtlasCollector||{},v="function"==typeof d.debug?d.debug:d.debug&&console&&console.log?console.log:function(){},p=d.properties||{},m=d.cacheKey||"DAPROPS",h=d.cookieName||"DAPROPS",y=d.cacheType||"cookie",j=d.criticalProperties||o.a.criticalProperties,b=d.waitMilliSecondsToBulkSendProperties||3e3,g=d.cookieExpiryDays||1,w=d.cacheExpiryDays||1,k=d.dontStopPropertyUpdateEvents||!1,E=d.cookieDomain||null,O=d.cookiePath||"/",S="function"==typeof d.onPropertiesUpdate?d.onPropertiesUpdate:function(){},x=!1,T=function(e,t){x&&!k||(S(e,t),x=!0)},P=[],D=!1,A=function(e,t){(function(e,t){if(!Array.isArray(e)||!Array.isArray(t))return!1;for(var n=0;n<e.length;n++)if(!~t.indexOf(e[n]))return!1;return!0})(j,P)&&(D&&(clearTimeout(D),D=!1),T(e,t))},C=function(){return(new Date).getTime()-l+"ms"},L=function(){return function(e){var t,n,r="";for(var o in e)e.hasOwnProperty(o)&&"timeTook"!==o&&"E"!==o&&"S"!==o&&(r+=(t="boolean"==typeof(n=e[o])?"b":"number"==typeof n&&n%1==0?"i":"s")+o+":"+("b"===t?e[o]+0:e[o])+"|");return"bS:0|"+r+"bE:0"}(R)},R={csVersion:"2.4.6"},M=function(e,t,n){if(P.push(e),void 0!==t&&null!=t&&t!==n)switch("object"==typeof t?function(e,t,n){var r=a(t,e),o=void 0;for(var i in r)if(void 0!==r[i]){var c=i.indexOf("._.");n[~c?i.substring(c+u):i]=r[i],o=o||!!r[i]}}(e,t,R):R[e]=t,y){case"cookie":!function(e,t){var n=t.cookieName,r=t.cookieExpiryDays,o=t.cookieDomain,c=t.cookiePath;if(!Object(i.default)())return!1;var u=new Date;u.setDate(u.getDate()+r);var a="";null!=o&&(a=";domain="+o),document.cookie=n+'="'+e.replace(/;/g,"~")+'";expires='+u.toUTCString()+";path="+c+a}(L(),{cookieName:h,cookieDomain:E,cookieExpiryDays:g,cookiePath:O});break;case"local-storage":!function(e,t,n){if(!Object(c.default)())return!1;var r=(new Date).getTime()+864e5*n;window.localStorage.setItem(t,JSON.stringify({value:e,expiry:r}))}(L(),m,w)}R.timeTook=C(),A(R,L())},U=t.default={cookieName:h,cookieDomain:E,cookieExpiryDays:g,cookiePath:O,onPropertiesUpdate:S,csVersion:"2.4.6",getPropertiesAsString:L,getProperties:function(){return R},properties:R,debug:v,cacheProperty:M};f(y,m,0,h,R),P.push.apply(P,Object.keys(R)),s(R),s(R,p,!0),D=setTimeout((function(){R.timeTook=C(),T(R,L())}),b)},function(e,t,n){"use strict";var r=n(10),o=(n(2),["Webkit","Moz","O","ms","Khtml"]);t.a=function(e){var t=e.charAt(0).toUpperCase()+e.substr(1),n=(e+" "+o.join(t+" ")+t).split(" ");return Object(r.a)(n)}},function(e,t,n){"use strict";var r=["webkit","moz","o","ms","khtml"];t.a=function(e,t){if(!!e[t])return!0;for(var n=t.charAt(0).toUpperCase()+t.substr(1),o=-1,i=r.length;++o<i;)if(e[r[o]+n])return!0;return!1}},function(e,t,n){"use strict";n.r(t);var r=navigator&&navigator.connection||{};t.default=r},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!window.CanvasRenderingContext2D}},function(e,t){e.exports={criticalProperties:["deviceAspectRatio","devicePixelRatio","js.webGlRenderer","screenWidthHeight","rendererRef","audioRef","js.deviceMotion","html.video.ap4x","html.video.av1"]}},function(e,t,n){"use strict";t.a=function(e,t){t=t||5381;for(var n=0;n<e.length;n++)t=33*t^e.charCodeAt(n);return String(t>>>0)}},function(e,t,n){"use strict";n.r(t),t.default=function(){return void 0===navigator.cookieEnabled&&"string"==typeof document.cookie||!!navigator.cookieEnabled}},function(e,t,n){"use strict";var r=n(2);t.a=function(e){if(!document.createElement)return null;if(null==r.default.divStyle&&(r.default.divStyle=document.createElement("div").style),void 0===r.default.divStyle)return null;for(var t in e)if(void 0!==r.default.divStyle[e[t]])return!0;return!1}},function(e,t,n){"use strict";t.a=function(e,t){if(void 0!==window.matchMedia)for(var n in t){if(window.matchMedia("("+e+":"+t[n]+")").matches)return t[n]+""}}},function(e,t,n){"use strict";t.a=function(e){if(document.createElement)try{var t=document.createElement("video");if(t.canPlayType)switch(t.canPlayType(e)){case"":return!1;case"probably":return!0}}catch(e){}}},function(e,t,n){"use strict";n.r(t),t.default=function(){try{return!!localStorage.getItem}catch(e){return!1}}},function(e,t,n){"use strict";n.r(t),t.default=function(){try{return window.screen.width+"/"+window.screen.height}catch(e){}}},function(e,t,n){var r={"./audioRef.js":16,"./ch.bitness.js":17,"./ch.browserFullVersionList.js":18,"./ch.browserList.js":19,"./ch.model.js":20,"./ch.platform.js":21,"./ch.platformVersion.js":22,"./connectionType.js":23,"./cookieSupport.js":9,"./css.animations.js":24,"./css.columns.js":25,"./css.transforms.js":26,"./css.transitions.js":27,"./deviceAspectRatio.js":28,"./devicePixelRatio.js":29,"./displayColorDepth.js":30,"./downlink.js":31,"./effectiveConnectionType.js":32,"./flashCapable.js":33,"./html.audio.js":34,"./html.canvas.js":6,"./html.inlinesvg.js":35,"./html.svg.js":36,"./html.video.ap4x.js":37,"./html.video.av1.js":38,"./html.video.js":39,"./js.accessDom.js":40,"./js.applicationCache.js":41,"./js.battery.js":42,"./js.deviceMotion.js":43,"./js.deviceOrientation.js":44,"./js.forceTouch.js":45,"./js.geoLocation.js":46,"./js.indexedDB.js":47,"./js.json.js":48,"./js.localStorage.js":13,"./js.modifyCss.js":49,"./js.modifyDom.js":50,"./js.querySelector.js":51,"./js.sessionStorage.js":52,"./js.supportBasicJavaScript.js":53,"./js.supportConsoleLog.js":54,"./js.supportEventListener.js":1,"./js.supportEvents.js":71,"./js.touchEvents.js":55,"./js.webGl.js":56,"./js.webGlRenderer.js":57,"./js.webSockets.js":58,"./js.webSqlDatabase.js":59,"./js.webWorkers.js":60,"./js.xhr.js":61,"./mouseActivity.js":62,"./orientation.js":63,"./platformArchitecture.js":64,"./rendererRef.js":65,"./requestingMobileUx.js":66,"./roundTripTime.js":67,"./saveData.js":68,"./screenWidthHeight.js":14,"./timeZone.js":69,"./userMedia.js":70};function o(e){var t=i(e);return n(t)}function i(e){if(!n.o(r,e)){var t=new Error("Cannot find module '"+e+"'");throw t.code="MODULE_NOT_FOUND",t}return r[e]}o.keys=function(){return Object.keys(r)},o.resolve=i,e.exports=o,o.id=15},function(e,t,n){"use strict";n.r(t);var r=n(8);t.default=function(e,t){if(void 0!==e)return t(e);var n=null;try{if(!(n=new(window.OfflineAudioContext||window.webkitOfflineAudioContext)(1,44100,44100)))return t();var o=n.createOscillator();o.type="triangle",o.frequency.value=1e4;var i=n.createDynamicsCompressor();i.threshold&&(i.threshold.value=-50),i.knee&&(i.knee.value=40),i.ratio&&(i.ratio.value=12),i.reduction&&(i.reduction.value=-20),i.attack&&(i.attack.value=0),i.release&&(i.release.value=.25),o.connect(i),i.connect(n.destination),o.start(0),n.startRendering(),n.oncomplete=function(e){for(var n=!1,o=0;o<e.renderedBuffer.length;o++)n=Object(r.a)(e.renderedBuffer.getChannelData(0)[o].toString(),n);n&&t(n),i.disconnect()}}catch(e){t()}}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){if(void 0!==e)return t(e);n(0).default.then((function(e){t(e["ch.bitness"])}))}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){if(void 0!==e)return t(e);n(0).default.then((function(e){t(e["ch.browserFullVersionList"])}))}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){if(void 0!==e)return t(e);n(0).default.then((function(e){t(e["ch.browserList"])}))}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){if(void 0!==e)return t(e);n(0).default.then((function(e){t(e["ch.model"])}))}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){n(0).default.then((function(e){t(e["ch.platform"])}))}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){if(void 0!==e)return t(e);n(0).default.then((function(e){t(e["ch.platformVersion"])}))}},function(e,t,n){"use strict";n.r(t),t.default=function(){return n(5).default.type}},function(e,t,n){"use strict";n.r(t);var r=n(3);t.default=function(){return Object(r.a)("animationName")}},function(e,t,n){"use strict";n.r(t);var r=n(3);t.default=function(){return Object(r.a)("columnCount")}},function(e,t,n){"use strict";n.r(t);var r=n(10);t.default=function(){return!!Object(r.a)(["transformProperty","WebkitTransform","MozTransform","OTransform","msTransform"])}},function(e,t,n){"use strict";n.r(t);var r=n(3);t.default=function(){return Object(r.a)("transitionProperty")}},function(e,t,n){"use strict";n.r(t);var r=n(11),o=n(14);t.default=function(e){if(void 0!==e)return e;var t=["2/3","40/71","3/4","16/9","16/10","375/667","9/16","10/16","375/812","207/448"],n=Object(o.default)();return n&&t.push(n),Object(r.a)("device-aspect-ratio",t)}},function(e,t,n){"use strict";n.r(t),t.default=function(){var e=1;try{e=window.devicePixelRatio}catch(e){}return e+""}},function(e,t,n){"use strict";n.r(t),t.default=function(){return screen.colorDepth}},function(e,t,n){"use strict";n.r(t),t.default=function(){return n(5).default.downlink}},function(e,t,n){"use strict";n.r(t),t.default=function(){return n(5).default.effectiveType}},function(e,t,n){"use strict";n.r(t),t.default=function(){try{if(navigator.plugins&&navigator.plugins.length>0&&navigator.mimeTypes){var e="application/x-shockwave-flash";if(navigator.mimeTypes[e]&&navigator.mimeTypes[e].enabledPlugin)return!0}if("function"==typeof ActiveXObject||"object"==typeof ActiveXObject){var t=["ShockwaveFlash.ShockwaveFlash.7","ShockwaveFlash.ShockwaveFlash.7","ShockwaveFlash.ShockwaveFlash"];for(var n in t)try{return new ActiveXObject(t[n]),!0}catch(e){}}}catch(e){}return!1}},function(e,t,n){"use strict";n.r(t),t.default=function(e){if(void 0!==e)return e;if(!document.createElement)return!1;var t=document.createElement("audio");try{if(t.canPlayType){var n={};return n.ogg=""!==t.canPlayType('audio/ogg; codecs="vorbis"'),n.mp3=""!==t.canPlayType("audio/mpeg;"),n.wav=""!==t.canPlayType('audio/wav; codecs="1"'),n.m4a=""!==t.canPlayType("audio/x-m4a;"),n.m4a=""!=t.canPlayType("audio/aac;"),n}}catch(e){}return!1}},function(e,t,n){"use strict";n.r(t);t.default=function(e){if(void 0!==e)return e;if(!document.createElement)return!1;var t=document.createElement("div");return t.innerHTML="<svg/>","http://www.w3.org/2000/svg"==(t.firstChild&&t.firstChild.namespaceURI)}},function(e,t,n){"use strict";n.r(t);t.default=function(){return!!document.createElementNS&&!!document.createElementNS("http://www.w3.org/2000/svg","svg").createSVGRect}},function(e,t,n){"use strict";n.r(t);var r=n(12);t.default=function(e){return void 0!==e?e:Object(r.a)('video/quicktime; codecs="ap4x"')}},function(e,t,n){"use strict";n.r(t);var r=n(12);t.default=function(e){return void 0!==e?e:Object(r.a)('video/mp4; codecs="av01.0.05M.08"')}},function(e,t,n){"use strict";n.r(t),t.default=function(e){if(void 0!==e)return e;if(!document.createElement)return!1;var t=document.createElement("video");try{if(t.canPlayType){var n={};n.ogg=""!==t.canPlayType('video/ogg; codecs="theora"');var r='video/mp4; codecs="avc1.42E01E';return n.h264=""!==t.canPlayType(r+'"')||""!==t.canPlayType(r+', mp4a.40.2"'),n.webm=""!==t.canPlayType('video/webm; codecs="vp8, vorbis"'),n}}catch(e){}return!1}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!document.getElementById&&!!document.getElementsByTagName&&!!document.getElementsByName}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!window.applicationCache}},function(e,t,n){"use strict";n.r(t),t.default=function(){var e=navigator.battery||navigator.webkitBattery||navigator.mozBattery;if(e)return e}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!window.DeviceMotionEvent}},function(e,t,n){"use strict";n.r(t);var r=n(1);t.default=function(e,t){if(void 0!==e)return t(e);if(!Object(r.default)())return t(!1);window.addEventListener("deviceorientation",(function e(n){null!=n&&null!=n.alpha?t(!0):null==n&&t(!1),window.removeEventListener("deviceorientation",e,{once:!0,capture:!0})}),{once:!0,capture:!0})}},function(e,t,n){"use strict";n.r(t);var r=n(1);t.default=function(e,t){if(void 0!==e)return t(e);if(!Object(r.default)())return t(!1);var n=!1,o=function e(r){switch(r.type){case"touchforcechange":try{var o=r.touches[0];"direct"==o.touchType&&o.force>=.01&&(n=!0,t(!0)),window.removeEventListener("touchforcechange",e,!1)}catch(r){}break;case"touchend":n||t(!1);try{window.removeEventListener("touchend",e,!1)}catch(r){}}};window.addEventListener("touchforcechange",o,{once:!0,capture:!0}),window.addEventListener("touchend",o,{once:!0,capture:!0})}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!navigator.geolocation}},function(e,t,n){"use strict";n.r(t);var r=n(4);t.default=function(){return Object(r.a)(window,"indexedDB")}},function(e,t,n){"use strict";n.r(t),t.default=function(){return"object"==typeof JSON&&"function"==typeof JSON.stringify}},function(e,t,n){"use strict";n.r(t),t.default=function(){if(!document.createElement)return!1;var e=document.createElement("div");return"object"==typeof e.style&&void 0!==e.style.display&&(e.style.display="none","none"==e.style.display)}},function(e,t,n){"use strict";n.r(t),t.default=function(){if(!document.createElement||!document.appendChild)return!1;var e=document.createElement("span"),t=document.createElement("div");return t.appendChild(e),t==e.parentNode}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!document.querySelector}},function(e,t,n){"use strict";n.r(t),t.default=function(){try{return!!sessionStorage.getItem}catch(e){return!1}}},function(e,t,n){"use strict";n.r(t),t.default=function(){return"function"==typeof alert&&"function"==typeof confirm&&"function"==typeof setTimeout&&"function"==typeof setInterval&&(!!document.location&&!!document.forms)}},function(e,t,n){"use strict";n.r(t),t.default=function(){return"object"==typeof console&&"function"==typeof console.log}},function(e,t,n){"use strict";n.r(t),t.default=function(){return"ontouchstart"in window||navigator&&navigator.pointerEnabled&&navigator.msMaxTouchPoints>1}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!window.WebGLRenderingContext}},function(e,t,n){"use strict";n.r(t);var r=n(6);t.default=function(e){if(void 0!==e)return e;if(Object(r.default)()){var t=document.createElement("canvas"),n=t.getContext("webgl")||t.getContext("experimental-webgl");if(n){var o=n.getExtension("WEBGL_debug_renderer_info");if(o){var i=n.getParameter(o.UNMASKED_RENDERER_WEBGL);if(i)return i}}}}},function(e,t,n){"use strict";n.r(t);var r=n(4);t.default=function(){return Object(r.a)(window,"WebSocket")}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!window.openDatabase}},function(e,t,n){"use strict";n.r(t),t.default=function(){return!!window.Worker}},function(e,t,n){"use strict";n.r(t),t.default=function(){return"function"==typeof XMLHttpRequest||"object"==typeof XMLHttpRequest}},function(e,t,n){"use strict";n.r(t);var r=n(1);t.default=function(e,t){if(void 0!==e)return t(e);if(!Object(r.default)())return t(!1);window.addEventListener("mousemove",(function e(){t(!0);try{window.removeEventListener("mousemove",e,!1)}catch(e){}}),{once:!0,capture:!0})}},function(e,t,n){"use strict";n.r(t),t.default=function(){return window.orientation}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){if(void 0!==e)return t(e);n(0).default.then((function(e){var n=e.platformArchitecture;t(n)}))}},function(e,t,n){"use strict";n.r(t);var r=n(6),o=n(11),i=n(8);t.default=function(e){if(void 0!==e)return e;if(Object(r.default)()){var t=document.createElement("canvas"),n=t.getContext("2d"),c="DeviceAtlas";return n.textBaseline="top",n.font="16px 'Arial'",n.textBaseline="alphabetic",n.rotate(.05),n.fillStyle="#f60",n.fillRect(125,1,62,20),n.fillStyle="#069",n.fillText(c,2,15),n.fillStyle="rgba(102, 200, 0, 0.7)",n.fillText(c,4,17),n.shadowBlur=10,n.shadowColor="blue",n.rotate(-.15),n.fillRect(-20,25,234,5),("p3"===Object(o.a)("color-gamut",["p3"])?"1":"0")+Object(i.a)(t.toDataURL())}}},function(e,t,n){"use strict";n.r(t),t.default=function(e,t){if(void 0!==e)return t(e);n(0).default.then((function(e){var n=e.requestingMobileUx;t(n)}))}},function(e,t,n){"use strict";n.r(t),t.default=function(){return n(5).default.rtt}},function(e,t,n){"use strict";n.r(t),t.default=function(){return n(5).default.saveData}},function(e,t,n){"use strict";n.r(t),t.default=function(){var e;if("undefined"!=typeof Intl)return null==(e=Intl.DateTimeFormat())||null==(e=e.resolvedOptions())?void 0:e.timeZone}},function(e,t,n){"use strict";n.r(t);var r=n(4);t.default=function(){return Object(r.a)(navigator,"getUserMedia")}},function(e,t,n){"use strict";n.r(t);var r=function(e,t){if(!document.createElement)return!1;var n=document.createElement(t),r=(e="on"+e)in n;return r||(n.setAttribute||(n=document.createElement("div")),n.setAttribute&&n.removeAttribute&&(n.setAttribute(e,""),r="function"==typeof n[e])),r};t.default=function(){var e={submit:"form",click:"input",change:"input",load:"img",select:"input"};for(var t in e)if(!r(t,e[t]))return!1;return!0}}]).default;
/* End: lib/deviceatlas.min.js */

        _DeviceAtlasCollector = DeviceAtlasCollector; // this will be there after the lib is imported by grunt.

        return _DeviceAtlasCollector;
    }

    // If DeviceAssure Library is already initialised, then we don't need to do anything.
    if (DeviceValidation && DeviceValidation.check) {
        window.DeviceValidation = DeviceValidation;
        _DeviceValidation = window.DeviceValidation;

        return _DeviceValidation;
    }

    _initDA();

    if (_getDeviceAtlasCollector().cacheType || _getDeviceAtlasCollector().onPropertiesUpdate) {
        _detectApis();
        _collect(MESSAGE_TYPES.ON_LOAD, false);
    }

    window.DeviceValidation = _DeviceValidation;

    return _DeviceValidation;

})(window, document, navigator);
